import { Theme } from "@mui/material";
import { ColDef } from "ag-grid-community";
import { TimeframeTypeEnum } from "../../../common/enums/TimeframeTypeEnum";
import { UIViewEnum } from "../../../common/enums/UIViewEnum";
import { IBaseGridColumnDefinition } from "../../../common/models/BaseGridColumnDefinition";
import { getTagRoute } from "../../../common/utilities/RouteHelpers";
import { getTimeframeTimeDisplayText } from "../../../common/utilities/TimeframeUtilities";
import { RowDef } from "../../../components/ag-grid-extensions/models/RowDef";
import { StandardCellData } from "../../../components/ag-grid-extensions/renderers/StandardCellRenderer";
import { createTextColumn, createTimestampColumn } from "../../../components/summary-listing-grid/ColumnCreators";
import { IAlarmLynxGridModel, IAlarmLynxGridRow } from "../../../services/summary-listing-data-service/AlarmLynxSummaryModels";

export type AlarmLynxRow = RowDef & {
    deviceID: number;
    tagID: number;
    cell0: StandardCellData;
    cell1: StandardCellData;
    cell2: StandardCellData;
    cell3: StandardCellData;
};

export function createGridData(sourceData: IAlarmLynxGridModel | undefined, groupID: number, theme: Theme) {
    const cols: Array<ColDef> = createGridColumns(sourceData, theme);
    const rows: Array<RowDef> = createGridRows(sourceData, groupID);
    const data = cols && rows
        ? {
            rows: rows,
            cols: cols
        } : {
            rows: new Array<RowDef>(),
            cols: new Array<ColDef>()
        };
    return data;
}

export function createGridRows(sourceData: IAlarmLynxGridModel | undefined, groupID: number): Array<AlarmLynxRow> {
    return sourceData?.rows.map((row: IAlarmLynxGridRow) => {
        return {
            deviceID: row.deviceID,
            tagID: row.tagID,
            cell0: {
                displayText: row.deviceName,
                // TODO: this has to be changed to redirect                
                href: getTagRoute(UIViewEnum.Tag_Details, groupID, undefined, row.deviceID, undefined, undefined, row.tagID)
            },
            cell1: {
                displayText: row.tagName,
            },
            cell2: {
                displayText: row.currentStatus.displayText,
                dataValue: row.currentStatus.dataValue
            },
            cell3: {
                displayText: getTimeframeTimeDisplayText(TimeframeTypeEnum.None, row.timestamp),
                timestamp: typeof row.timestamp === "string" ? new Date(row.timestamp) : row.timestamp
            }
        };
    }) ?? new Array<AlarmLynxRow>();
}

export function createGridColumns(sourceData: IAlarmLynxGridModel | undefined, theme: Theme): Array<ColDef> {

    function alarmLynxStatusCellStyler(params: any): any {
        const cell = params.colDef.cellRendererParams.cellDataGetter(params);

        const style: { [key: string]: string; } = {};
        if (cell?.dataValue === 1) {
            style.color = theme.palette.error.main;
            style.fontWeight = "bold";
        } else {
            style.color = theme.palette.success.main;
        }

        return style;
    }

    return sourceData?.columns.map((col: IBaseGridColumnDefinition, index: number) => {

        switch (index) {
            case 0: // Device Name
                return createTextColumn({ field: `cell${index}`, headerName: col.headerText, initialSort: "asc", initialSortIndex: 0, footerAggregate: col.aggregate });
            case 1: // Tag Name
                return createTextColumn({ field: `cell${index}`, headerName: col.headerText, initialSort: "asc", initialSortIndex: 1 });
            case 2: // Current Status
                return createTextColumn({ field: `cell${index}`, headerName: col.headerText, styler: alarmLynxStatusCellStyler });
            case 3: // Timestamp
                return createTimestampColumn({ field: `cell${index}`, headerName: col.headerText });
            default:
                return createTextColumn({ field: `cell${index}`, headerName: col.headerText });
        }
    }) ?? new Array<ColDef>();
}

